/**
 * Core handle hash function extracted to avoid circular dependencies.
 * This file contains only the docEntityHandleHash function and its minimal dependencies.
 * @module
 */
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { DocEntityPrefix } from "building-types/handles/docEntityPrefix";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { generatedEntityHandleHash } from "../../generatedEntityHandleHash";
import { autoDimensionHandleHashNew } from "../docEntityHandleToolsAutoDimension";

export function docEntityHandleHash(e: DocEntityHandle): string {
  if (!e) {
    return "";
  }

  switch (e.type) {
    case "GROUP":
      return `${DocEntityPrefix.Group}.${e.id}`;
    case "DOC_REF":
      return `${DocEntityPrefix.DocRef}.${e.id}`;
    case "BUILDER_REF":
      return `${DocEntityPrefix.BuilderRef}.${e.id}`;
    case "OPTION_SET_REF":
      return `${DocEntityPrefix.OptionSetRef}.${e.id}`;
    case "LEVEL":
      return `${DocEntityPrefix.Level}.${e.id}`;
    case "COMPOSITE_LEVEL":
      return `${DocEntityPrefix.CompositeLevel}.${e.id}`;
    case "BLOCK":
      return `${DocEntityPrefix.Block}.${e.id}`;
    case "FACADE":
      return `${DocEntityPrefix.Facade}.${e.id}`;
    case "FACADE_SPAN_STRATUM":
      return `${DocEntityPrefix.FacadeSpanStrata}.${e.id}`;
    case "FACADE_SWEEP_STRATUM":
      return `${DocEntityPrefix.FacadeSweepStrata}.${e.id}`;
    case "EDGE_BEHAVIOR":
      return `${DocEntityPrefix.EdgeBehavior}.${e.id}`;
    case "WALL_PLANE_ASSEMBLY":
      return `${DocEntityPrefix.WallPlaneAssembly}.${e.id}`;
    case "WALL_PLANE_FILTER":
      return `${DocEntityPrefix.WallPlaneFilter}.${e.id}`;
    case "WALL_PLANE_RULE":
      return `${DocEntityPrefix.WallPlaneRule}.${e.id}`;
    case "WALL_PLANE_TRIM":
      return `${DocEntityPrefix.WallPlaneTrim}.${e.id}`;
    case "ROOM":
      return `${DocEntityPrefix.Room}.${e.id}`;
    case "ROOM_LAYOUT":
      return `${DocEntityPrefix.RoomLayout}.${e.id}`;
    case "ROOM_LAYOUT_INSTANCE":
      return `${DocEntityPrefix.RoomLayoutInstance}.${e.id}`;
    case "STAIR_SYSTEM":
      return `${DocEntityPrefix.StairSystem}.${e.id}`;
    case "STAIR_BLOCK":
      return `${DocEntityPrefix.StairBlock}.${e.id}`;
    case "RAILING":
      return `${DocEntityPrefix.Railing}.${e.id}`;
    case "RAILING_POST":
      return `${DocEntityPrefix.RailingPost}.${e.id}`;
    case "ROOF_SYSTEM":
      return `${DocEntityPrefix.RoofSystem}.${e.id}`;
    case "ROOF":
      return `${DocEntityPrefix.Roof}.${e.id}`;
    case "ROOF_SIDE":
      return `${DocEntityPrefix.RoofSide}.${e.parent.id}.${e.side}`;
    case "ROOF_VOLUME":
      return `${DocEntityPrefix.RoofVolume}.${e.id}`;
    case "FOUNDATION_SYSTEM":
      return `${DocEntityPrefix.FoundationSystem}.${e.id}`;
    case "FOUNDATION":
      return `${DocEntityPrefix.Foundation}.${e.id}`;
    case "UNDERLAY":
      return `${DocEntityPrefix.Underlay}.${e.id}`;
    case "ASSET":
      return `${DocEntityPrefix.Asset}.${e.id}`;
    case "ELEVATION":
      return `${DocEntityPrefix.Elevation}.${e.id}`;
    case "REFERENCE_GEOM":
      return `${DocEntityPrefix.ReferenceGeo}.${e.id}`;
    case "PALETTE":
      return `${DocEntityPrefix.Palette}.${e.id}`;
    case "DATA_TABLE":
      return `${DocEntityPrefix.DataTable}.${e.id}`;
    case "DATA_SEQUENCE":
      return `${DocEntityPrefix.DataSequence}.${e.id}`;
    case "CASEWORK":
      return `${DocEntityPrefix.Casework}.${e.id}`;
    case "CEILING":
      return `${DocEntityPrefix.Ceiling}.${e.id}`;
    case "PROP_INSTANCE":
      return `${DocEntityPrefix.PropInstance}.${e.id}`;
    case "ANNOTATION":
      return `${DocEntityPrefix.Annotation}.${e.id}`;
    case "CATALOG_ITEM":
      return `${DocEntityPrefix.CatalogItem}.${e.itemId}`;
    case "SHAPE":
      switch (e.parent.type) {
        case "ANNOTATION":
        case "ROOM":
        case "REFERENCE_GEOM":
        case "STAIR_BLOCK":
        case "ROOF":
        case "RAILING":
        case "CEILING":
        case "FLOOR_FINISH":
        case "MEASUREMENT":
        case "SITE_SHAPE":
        case "ROOM_LAYOUT_INSTANCE":
        case "VIEWPORT":
        case "FLOOR":
        case "FLOOR_BAY":
        case "FLOOR_SPLIT_LINE":
          return `${DocEntityPrefix.Shape}.${e.parent.id}.${e.shapeId}`;
        case "FOUNDATION":
          return `${DocEntityPrefix.Shape}.${e.parent.id}.${e.shapeId}`;
        case "EDIT_MODE":
          return `${DocEntityPrefix.Shape}.${e.parent.editModeType}.${e.shapeId}`;
        default:
          assertUnreachable(e.parent);
      }
    case "EDGE":
      switch (e.parent.type) {
        case "ANNOTATION":
        case "ROOM":
        case "REFERENCE_GEOM":
        case "STAIR_BLOCK":
        case "ROOF":
        case "RAILING":
        case "CEILING":
        case "FLOOR_FINISH":
        case "MEASUREMENT":
        case "SITE_SHAPE":
        case "VIEWPORT":
        case "FLOOR":
        case "FLOOR_BAY":
        case "FLOOR_SPLIT_LINE": {
          const baseHandle = `${DocEntityPrefix.Edge}.${e.parent.id}.${e.shapeId}.${e.edgeId}`;
          if (e.shapeId === "opening" && "openingIndex" in e) {
            return `${baseHandle}.opening.${e.openingIndex}`;
          }
          if (e.shapeId === "outline" && "perimeterIndex" in e) {
            return `${baseHandle}.perimeter.${e.perimeterIndex}`;
          }
          return baseHandle;
        }
        case "FOUNDATION":
          return `${DocEntityPrefix.Edge}.${e.parent.id}.${e.shapeId}.${e.edgeId}`;
        case "EDIT_MODE":
          return `${DocEntityPrefix.Edge}.${e.parent.editModeType}.${e.shapeId}.${e.edgeId}`;
        case "ROOM_LAYOUT_INSTANCE":
          return `${DocEntityPrefix.Shape}.${e.parent.id}.${e.shapeId}`;
        default:
          assertUnreachable(e.parent);
      }
    case "VERTEX":
      switch (e.parent.type) {
        case "ANNOTATION":
        case "ROOM":
        case "REFERENCE_GEOM":
        case "STAIR_BLOCK":
        case "ROOF":
        case "RAILING":
        case "CEILING":
        case "FLOOR_FINISH":
        case "MEASUREMENT":
        case "VIEWPORT":
        case "SITE_SHAPE":
        case "FLOOR":
        case "FLOOR_BAY":
        case "FLOOR_SPLIT_LINE":
          return `${DocEntityPrefix.Vertex}.${e.parent.id}.${e.shapeId}.${e.vertexId}`;
        case "FOUNDATION":
          return `${DocEntityPrefix.Vertex}.${e.parent.id}.${e.shapeId}.${e.vertexId}`;
        case "EDIT_MODE":
          return `${DocEntityPrefix.Vertex}.${e.parent.editModeType}.${e.shapeId}.${e.vertexId}`;
        case "ROOM_LAYOUT_INSTANCE":
          return `${DocEntityPrefix.Shape}.${e.parent.id}.${e.shapeId}`;
        default:
          assertUnreachable(e.parent);
      }
    case "ELECTRICAL_DEVICE":
      return `${DocEntityPrefix.ElectricalDevice}.${e.id}`;
    case "ELECTRICAL_LAYOUT":
      return `${DocEntityPrefix.ElectricalLayout}.${e.id}`;
    case "ELECTRICAL_RULE":
      return `${DocEntityPrefix.ElectricalRule}.${e.settingsHandle.id}.${e.id}`;
    case "OPENING":
      return `${DocEntityPrefix.Opening}.${e.id}`;
    case "FLOOR":
      return `${DocEntityPrefix.Floor}.${e.id}`;
    case "FLOOR_FINISH":
      return `${DocEntityPrefix.FloorFinish}.${e.id}`;
    case "COLUMN":
      return `${DocEntityPrefix.Column}.${e.id}`;
    case "SETTINGS":
      return `${DocEntityPrefix.Settings}.${e.id}`;
    case "SETTINGS_SET":
      return `${DocEntityPrefix.SettingsSet}.${e.id}`;
    case "ENTITY_SETTINGS":
      return `${DocEntityPrefix.EntitySettings}.${e.id}`;
    case "OPTION_TEMPLATE":
      return `${DocEntityPrefix.OptionTemplate}.${e.id}`;
    case "OPTION_SET_OVERRIDES":
      return `${DocEntityPrefix.OptionSetOverrides}.${e.id}`;
    case "OPTION_SET":
      return `${DocEntityPrefix.OptionSet}.${e.id}`;
    case "OPTION":
      return `${DocEntityPrefix.Option}.${e.optionSetId}.${e.optionKey}`;
    case "OPTION_RULE":
      return `${DocEntityPrefix.OptionRule}.${e.id}`;
    case "OPTION_EFFECT":
      return `${DocEntityPrefix.OptionEffect}.${e.id}`;
    case "OPTION_LOCK":
      return `${DocEntityPrefix.OptionLocks}.${e.id}`;
    case "INSTANCE_OPTION_SET":
      return `${DocEntityPrefix.InstanceOptionSet}.${e.id}`;
    case "SHEET":
      return `${DocEntityPrefix.Sheet}.${e.id}`;
    case "DRAWING_REF":
      return `${DocEntityPrefix.DrawingRefs}.${e.id}`;
    case "DORMER":
      return `${DocEntityPrefix.Dormer}.${e.id}`;
    case "PLAN_VIEW":
      return `${DocEntityPrefix.PlanView}.${e.id}`;
    case "VIEW_OVERRIDE":
      return `${DocEntityPrefix.ViewOverride}.${e.id}`;
    case "SELECTOR":
      return `${DocEntityPrefix.HandleBasedSelector}.${e.id}`;
    case "VIEW_SELECTOR":
      return `${DocEntityPrefix.ViewSelector}.${e.id}`;
    case "VIEW_3D":
      return `${DocEntityPrefix.View3D}.${e.id}`;
    case "ELEVATION_VIEW":
      return `${DocEntityPrefix.ElevationView}.${e.id}`;
    case "SKYLIGHT":
      return `${DocEntityPrefix.Skylight}.${e.id}`;
    case "ROOF_OPENING":
      return `${DocEntityPrefix.RoofOpening}.${e.id}`;
    case "MODIFIER":
      return `${DocEntityPrefix.Modifier}.${e.id}`;
    case "EDGE_MODIFIER":
      return `${DocEntityPrefix.EdgeModifier}.${e.id}`;
    case "MEASUREMENT":
      return `${DocEntityPrefix.Measurement}.${e.id}`;
    case "SITE":
      return `${DocEntityPrefix.Site}.${e.id}`;
    case "SITE_SHAPE":
      return `${DocEntityPrefix.SiteShape}.${e.id}`;
    case "EDIT_MODE":
      return `${DocEntityPrefix.EditMode}.${e.editModeType}`;
    case "GENERATED_ENTITY":
      return generatedEntityHandleHash(e.entity);
    case "AUTO_DIMENSION":
      return autoDimensionHandleHashNew(e);
    case "PLAN_SET_TEMPLATE":
      return `${DocEntityPrefix.PlanSetTemplate}.${e.id}`;
    case "PLAN_SET_TEMPLATE_CONFIG":
      return `${DocEntityPrefix.PlanSetTemplateConfig}.${e.id}`;
    case "CONTENT_TEMPLATE":
      return `${DocEntityPrefix.ContentTemplate}.${e.id}`;
    case "CONTENT_TEMPLATE_INSTANCE":
      return `${DocEntityPrefix.ContentTemplateInstance}.${e.id}`;
    case "CONTENT_REPLICATOR":
      return `${DocEntityPrefix.ContentReplicator}.${e.id}`;
    case "LOCAL_PARAMETER":
      return `${DocEntityPrefix.Variable}.${e.id}`;
    case "GLOBAL_PARAMETER":
      return `${DocEntityPrefix.GlobalParameter}.${e.id}`;
    case "SYSTEM_PARAMETER":
      return `${DocEntityPrefix.SystemParameter}.${e.id}`;
    case "PRESET":
      return `${DocEntityPrefix.Preset}.${e.id}`;
    case "PROFILE":
      return `${DocEntityPrefix.Profile}.${e.id}`;
    case "VIEWPORT":
      return `${DocEntityPrefix.Viewport}.${e.id}`;
    case "VIEW_TEMPLATE":
      return `${DocEntityPrefix.ViewTemplate}.${e.id}`;
    case "SECTION_VIEW":
      return `${DocEntityPrefix.SectionView}.${e.id}`;
    case "LABEL":
      return `${DocEntityPrefix.Label}.${e.id}`;
    case "REPORTABLE_UNIT_DESIGNATION":
      return `${DocEntityPrefix.ReportableUnitDesignation}.${e.id}`;
    case "VIEW_TEMPLATE_OVERRIDE":
      return `${DocEntityPrefix.ViewTemplateOverride}.${e.id}`;
    case "EFFECT":
      return `${DocEntityPrefix.Effect}.${e.id}`;
    case "DATUM":
      return `${DocEntityPrefix.Datum}.${e.id}`;
    case "RULE_BASED_SELECTOR":
      return `${DocEntityPrefix.RuleBasedSelector}.${e.id}`;
    case "RULE_BASED_SELECTOR_INSTANCE":
      return `${DocEntityPrefix.RuleBasedSelectorInstance}.${e.id}`;
    case "PROP":
      if (e.settingsHandle?.id) {
        return `${DocEntityPrefix.PropAttributes}.${e.settingsHandle.id}.${e.id}`;
      } else {
        return `${DocEntityPrefix.PropAttributes}.${e.id}`;
      }
    case "INDETERMINATE_ENTITY_TYPE":
      return `${DocEntityPrefix.IndeterminateId}.${e.id}`;
    case "FRAMING_COMPONENT":
      return `${DocEntityPrefix.FramingComponent}.${e.id}`;
    case "FOUNDATION_ASSEMBLY":
      return `${DocEntityPrefix.FoundationAssembly}.${e.id}`;
    case "FOUNDATION_COMPONENT":
      return `${DocEntityPrefix.FoundationComponentEntity}.${e.id}`;
    case "HEADER_ASSEMBLY":
      return `${DocEntityPrefix.HeaderAssembly}.${e.id}`;
    case "COMPONENT":
      return `${DocEntityPrefix.Component}.${e.id}`;
    case "COMPONENT_INSTANCE":
      return `${DocEntityPrefix.ComponentInstance}.${e.id}`;
    case "FRAME_FLOOR_ASSEMBLY":
      return `${DocEntityPrefix.FrameFloorAssembly}.${e.id}`;
    case "SLAB_FLOOR_ASSEMBLY":
      return `${DocEntityPrefix.SlabFloorAssembly}.${e.id}`;
    case "SLAB_COMPONENT":
      return `${DocEntityPrefix.SlabComponent}.${e.id}`;
    case "MASONRY_COMPONENT":
      return `${DocEntityPrefix.MasonryComponent}.${e.id}`;
    case "SHEATHING_COMPONENT":
      return `${DocEntityPrefix.SheathingComponent}.${e.id}`;
    case "FLOOR_SYSTEM":
      return `${DocEntityPrefix.FloorSystem}.${e.id}`;
    case "FLOOR_PERIMETER_OPENING":
      return `${DocEntityPrefix.FloorPerimeterOpening}.${e.floorId}.${e.openingIndex}`;
    case "FLOOR_BAY":
      return `${DocEntityPrefix.FloorBay}.${e.id}`;
    case "FLOOR_SPLIT_LINE":
      return `${DocEntityPrefix.FloorSplitLine}.${e.id}`;
    default:
      assertUnreachable(e);
  }
}
